/**
 * Sales Control › References switchboard config (Access frmCtrlSalesMainSubFormReferences
 * "Go to Page:" combo → 12 read-only datasheet pages). Column field keys MUST match the
 * backend SQL aliases in `sale_control_reference_pg._SALE_CONTROL_REFERENCE_SPECS` and the
 * baseline `sale_control_columns.py` (mode `ref-<pageKey>`).
 */
export type SaleControlReferencePageKey =
  | 'ServiceInvoices'
  | 'VirtualWH'
  | 'EXCalendar'
  | 'ValueUpdates'
  | 'Annex'
  | 'Delivery'
  | 'ListAndValo'
  | 'DeliveryPlace'
  | 'FuturesAllocation'
  | 'Certification'
  | 'Fixation'
  | 'CP'

export type SaleControlRefValueKind = 'text' | 'number' | 'date' | 'datetime'

export interface SaleControlRefColumn {
  field: string
  headerName: string
  kind?: SaleControlRefValueKind
  width?: number
  minWidth?: number
  precision?: number
  align?: 'left' | 'center' | 'right'
  proper?: boolean
  wrap?: boolean
}

export interface SaleControlRefPageConfig {
  pageKey: SaleControlReferencePageKey
  label: string
  sourceForm: string
  sourceQuery: string
  limit?: number
  columns: SaleControlRefColumn[]
}

// Order = Access combo `cbPreferencesList` RowSource order.
export const SALE_CONTROL_REFERENCE_PAGE_ORDER: SaleControlReferencePageKey[] = [
  'ServiceInvoices',
  'VirtualWH',
  'EXCalendar',
  'ValueUpdates',
  'Annex',
  'Delivery',
  'ListAndValo',
  'DeliveryPlace',
  'FuturesAllocation',
  'Certification',
  'Fixation',
  'CP',
]

const T = (field: string, headerName: string, extra: Partial<SaleControlRefColumn> = {}): SaleControlRefColumn => ({
  field,
  headerName,
  ...extra,
})
const N = (field: string, headerName: string, precision = 2): SaleControlRefColumn => ({
  field,
  headerName,
  kind: 'number',
  precision,
})
const D = (field: string, headerName: string): SaleControlRefColumn => ({ field, headerName, kind: 'date' })

const UPDATED_COLS: SaleControlRefColumn[] = [D('date_update', 'Updated Date'), T('person_update', 'Updated Person')]

// Fixation fix1..fix30 blocks (Date Fix N / Lots Fixed N / Level Fixed N).
const FIXATION_FIX_COLS: SaleControlRefColumn[] = Array.from({ length: 30 }, (_, i) => i + 1).flatMap((n) => [
  D(`fix${n}_date`, `Date Fix ${n}`),
  N(`fix${n}_lots_fixed`, `Lots Fixed ${n}`, 0),
  N(`fix${n}_level_fixed`, `Level Fixed ${n}`, 2),
])

export const SALE_CONTROL_REFERENCE_PAGE_CONFIGS: Record<SaleControlReferencePageKey, SaleControlRefPageConfig> = {
  ServiceInvoices: {
    pageKey: 'ServiceInvoices',
    label: 'Service Invoices',
    sourceForm: 'frmCtrlSalesQryInvoice_SuggestInvoice',
    sourceQuery: 'CtrlSalesQryInvoice_SuggestInvoice',
    // Thứ tự cột theo TabIndex của datasheet Access (form single-column, ColumnOrder
    // không set → TabIndex là thứ tự hiển thị). INV-Status ở giữa, KHÔNG ở cuối.
    columns: [
      T('service_code', 'ServiceCode'),
      T('service_company', 'ServiceCompany', { proper: true, wrap: true, width: 220 }),
      T('inv_no_merge', 'INV-No'),
      N('quantity', 'INV-Quantity'),
      N('price', 'INV-Price'),
      T('trang_thai_kiem_tra', 'INV-CheckingStatus'),
      D('ngay_hoa_don', 'INV-Date'),
      N('total_amount', 'TotalAmount (after tax)'),
      N('total_discount_amount', 'DiscountAmount'),
      N('total_amount_without_vat', 'TotalAmount'),
      N('total_vat_amount', 'VATAmount'),
      T('product_note', 'INV-Note', { wrap: true, width: 200 }),
      T('loai_hoa_don', 'INV-Type'),
      T('trang_thai_hoa_don', 'INV-Status'),
      T('phan_loai_chung_tu', 'VoucherType'),
      T('trang_thai_lap_chung_tu', 'VoucherStatus'),
      T('loai_tien', 'Currency'),
      T('refno_finance', 'VoucherNo'),
      T('inv_tab', 'INVTab'),
    ],
  },
  VirtualWH: {
    pageKey: 'VirtualWH',
    label: 'Virtual WH',
    sourceForm: 'frmCtrlSalesQryRefVirtualWH',
    sourceQuery: 'CtrlSalesQryRefVirtualWH',
    columns: [
      T('receipt_no', 'RDN No'),
      D('date_in', 'Date in'),
      T('do_no', 'DO No'),
      T('truck_no', 'Truck No'),
      T('supplier_code', 'From Supplier'),
      T('supplier_name', 'Supplier name', { proper: true, wrap: true, width: 200 }),
      T('client_code', 'To Client'),
      T('client_name', 'Client name', { proper: true, wrap: true, width: 200 }),
      T('warehouse_code', 'Warehouse Code'),
      T('warehouse_name', 'Warehouse Name', { wrap: true, width: 180 }),
      T('quality_code', 'Quality'),
      N('announced_quantity', 'Announced Quantity'),
      N('no_of_bag', 'No of Bag', 0),
      N('vgm_quantity_kg', 'VGM Quantiy'),
      N('mc', 'MC'),
      N('bb', 'BB'),
      N('brown', 'Brown'),
      N('cherry', 'Cherry'),
      N('overdry', 'Overdry'),
      N('fm', 'FM'),
      N('black', 'Black'),
      N('brokens', 'Brokens'),
      N('foreign_matter', 'Foreign'),
      N('insect', 'Insect'),
      N('immature', 'Immature'),
      N('old', 'Old'),
      N('mouldy', 'Mouldy'),
      N('white', 'White'),
      N('s18', 'S18'),
      N('s17', 'S17'),
      N('s16', 'S16'),
      N('s15', 'S15'),
      N('s14', 'S14'),
      N('s13', 'S13'),
      N('s12', 'S12'),
      N('under_s12', '<S12'),
      N('deduction_quantity_kg', 'Deduction (Kg)'),
      N('deduction_price_vnd', 'Deduction (VND)'),
      N('payment_quantity', 'Payment Quantity'),
      T('status', 'Status'),
      N('deduction_other', 'Deduction Other'),
    ],
  },
  EXCalendar: {
    pageKey: 'EXCalendar',
    label: 'EX Calendar',
    sourceForm: 'frmCtrlSalesQryRefEXCalendar',
    sourceQuery: 'CtrlSalesQryExCalendar',
    columns: [
      N('year', 'Year', 0),
      N('month', 'Month', 0),
      T('code', 'Code'),
      D('first_business_day', 'First Business Day'),
      T('full_symbol', 'Full MD'),
      D('last_business_day', 'Last Business Day'),
      D('first_notice_day', 'FND'),
      D('last_trading_day', 'LTD'),
      D('last_fixing_day', 'Last Fixing Day'),
      T('ex', 'EX'),
      ...UPDATED_COLS,
    ],
  },
  ValueUpdates: {
    pageKey: 'ValueUpdates',
    label: 'Value Updates',
    sourceForm: 'frmCtrlSalesQryRefValueUpdate',
    sourceQuery: 'CtrlSalesQryValueUpdate',
    columns: [
      D('date', 'Date'),
      T('code', 'Code'),
      T('description', 'Description', { wrap: true, width: 220 }),
      N('premium_vs_g2_5percent', 'Premium vs. G2 5%'),
      T('reason_of_change', 'Reason of change', { wrap: true, width: 220 }),
      ...UPDATED_COLS,
    ],
  },
  Annex: {
    pageKey: 'Annex',
    label: 'Annex',
    sourceForm: 'frmCtrlSalesQryRefAnnex',
    sourceQuery: 'ContractSalesQryAnnex',
    columns: [
      N('id', 'ID', 0),
      T('annex_no', 'Amendment No.'),
      D('annex_date', 'Amendment Date.'),
      T('contract_no', 'Contract Ref'),
      T('cp_code', 'Supplier Code'),
      T('name', 'Supplier', { proper: true, wrap: true, width: 200 }),
      T('content_before_change', 'Before change', { wrap: true, width: 220 }),
      T('content_after_change', 'After change', { wrap: true, width: 220 }),
      T('note', 'Note', { wrap: true, width: 200 }),
      N('price', 'Price'),
      N('quantity', 'Quantity'),
      ...UPDATED_COLS,
    ],
  },
  Delivery: {
    pageKey: 'Delivery',
    label: 'Delivery',
    sourceForm: 'frmCtrlSalesQryRefDelivery',
    sourceQuery: 'CtrlSalesQryDelivery',
    columns: [
      T('gdn_id', 'GDN ID'),
      D('date_out', 'Date out'),
      T('do_no', 'DO No.'),
      T('from_warehouse', 'From warehouse'),
      T('to_warehouse', 'To warehouse'),
      T('lot_name_buyer', "Lot name's buyer"),
      T('lot_id', 'Lot ID'),
      T('stack_id', 'Stack ID'),
      T('package', 'Package'),
      N('number_theory_of_bag', 'Number theory of bag', 0),
      N('real_bags', 'Real bags', 0),
      T('truck_no', 'Truck No.'),
      T('package_to_truck', 'package_to_truck'),
      N('gross_weight', 'gross_weight'),
      N('bags_on_truck', 'Bags on truck', 0),
      N('net_real_weight', 'Net real weight'),
      N('deduction_rate', 'Deduction rate'),
      T('supervisor', 'Supervisor'),
      N('bag_at_buyer', 'Bag at buyer', 0),
      N('net_weight_at_buyer', 'Net weight at buyer'),
      N('deduction_at_buyer', 'Deduction at buyer'),
      ...UPDATED_COLS,
    ],
  },
  ListAndValo: {
    pageKey: 'ListAndValo',
    label: 'List and Valo',
    sourceForm: 'frmCtrlSalesQryRefListAndValo',
    sourceQuery: 'CtrlSalesQryListAndValo',
    columns: [
      T('code', 'Code'),
      T('description', 'Description', { wrap: true, width: 200 }),
      T('bean_size', 'Bean Size'),
      T('phase', 'Phase'),
      T('category', 'Category'),
      T('ranked', 'Ranked'),
      T('special_requirements', 'Special Requirements', { wrap: true, width: 200 }),
      T('note', 'Note', { wrap: true, width: 200 }),
      T('description_on_contract', 'Description On Contract', { wrap: true, width: 220 }),
      ...UPDATED_COLS,
    ],
  },
  DeliveryPlace: {
    pageKey: 'DeliveryPlace',
    label: 'Delivery Place',
    sourceForm: 'frmCtrlSalesQryRefDeliveryPlace',
    sourceQuery: 'CtrlSalesQryDeliveryPlace',
    columns: [
      T('delivery_place', 'Delivery Place'),
      N('logistic_costs_to_fob', 'Logistic costs to FOB'),
      ...UPDATED_COLS,
    ],
  },
  FuturesAllocation: {
    pageKey: 'FuturesAllocation',
    label: 'Futures Allocation',
    sourceForm: 'frmCtrlSalesQryRefFuturesAllocation',
    sourceQuery: 'CtrlSalesQryFuturesAllocation',
    columns: [
      D('date', 'Date'),
      T('contract_id', 'Contract ID'),
      T('ps', 'P/S'),
      T('cp', 'CP'),
      N('kg', 'Kg'),
      T('type', 'Type'),
      N('lot_allocating', 'Lot allocating', 0),
      T('futures_id', 'Futures ID'),
      T('buysell', 'Buy/Sell'),
      T('month', 'Month'),
      N('price', 'Price'),
      N('balance', 'Balance'),
    ],
  },
  Certification: {
    pageKey: 'Certification',
    label: 'Certification',
    sourceForm: 'frmCtrlSalesQryRefCertification',
    sourceQuery: 'MdQryCertification',
    columns: [
      T('certification', 'Certification'),
      N('premium', 'Premium (New)'),
      D('max_date_change', 'Date Change (New)'),
      ...UPDATED_COLS,
    ],
  },
  Fixation: {
    pageKey: 'Fixation',
    label: 'Fixation',
    sourceForm: 'frmCtrlSalesQryRefFixation',
    sourceQuery: 'ContractSalesQryPTBFFixation',
    columns: [
      T('contract_sale', 'Contract No'),
      T('buyers_ref', "Buyer's Ref"),
      T('cp_code', 'Client Ref'),
      T('name', 'Client Name', { proper: true, wrap: true, width: 200 }),
      T('quality', 'Grade'),
      T('description', 'Description', { wrap: true, width: 200 }),
      N('mtons', 'Mtons'),
      N('no_of_lots', 'No. of Lots', 0),
      N('ptbf_diffs', 'PTBF Diffs'),
      T('futures_month', 'Futures Month'),
      N('balance_lots_tbf', 'Balance Lots TBF', 0),
      N('final_price', 'Final Price'),
      ...FIXATION_FIX_COLS,
      T('upload_txt', 'Is Uploaded?'),
      D('release_date', 'ReleaseDate'),
      T('txt_cus_sign', 'Customer Signed?'),
    ],
  },
  CP: {
    pageKey: 'CP',
    label: 'CP',
    sourceForm: 'frmCtrlSalesQryRefCP',
    sourceQuery: 'CtrlSalesQryCP',
    columns: [
      T('code', 'Code'),
      T('name_cp', 'Name', { proper: true, wrap: true, width: 220 }),
      T('short_name', 'Short Name'),
      T('address', 'Address', { wrap: true, width: 220 }),
      T('phone', 'Phone'),
      T('mobile', 'Mobile'),
      T('fax', 'Fax'),
      T('vat_code', 'VAT Code'),
      T('email', 'Email'),
      T('status', 'Status'),
      T('rep', 'Rep', { proper: true }),
      T('position', 'Position'),
      T('reg_no', 'Reg No'),
      T('issued_by', 'Issued By'),
      D('issuing_date', 'Issuing Date'),
      T('id_no', 'ID No'),
      T('id_place', 'ID Place'),
      D('id_date', 'ID Date'),
      T('note', 'Note', { wrap: true, width: 200 }),
      T('sale_offshore_onshore', 'Sale Offshore Onshore'),
      T('type_cp', 'Type'),
      T('status_cp', 'Using'),
      ...UPDATED_COLS,
    ],
  },
}
